import React, { useEffect, useRef, useState } from 'react'
import BpmnViewer from 'bpmn-js/lib/NavigatedViewer'
import CamundaBpmnModdle from 'camunda-bpmn-moddle/resources/camunda.json'
import sistemivModdleDescriptors from '../extensions/bpmn/descriptors/sistemiv.json'
import SDocumentation from '../extensions/bpmn/modules/documentation-viewer'
import { MinusIcon, PlusIcon, ViewfinderCircleIcon } from '@heroicons/react/24/outline'
import { useParams } from 'react-router-dom'
import MetadataService from '../../services/MetadataService'
import FormPreviewModal from '../modals/FormPreviewModal'
import { SpinnerIcon } from '@sistemiv/s-components'
import DocumentationModal from '../modals/DocumentationModal'
import RaciModal, { Raci } from '../modals/RaciModal'

type Props = {
  xml: string
}

const BpmnViewerComponent = ({ xml }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const viewerRef = useRef<any>(null)
  const { org, project } = useParams()
  const [isLoading, setIsLoading] = useState(false)
  const [parsedJson, setParsedJson] = useState<any>(null)
  const [formPreview, setFormPreview] = useState<boolean>(false)
  const [documentationPreview, setDocumentationPreview] = useState<boolean>(false)
  const [markdown, setMarkdown] = useState<string>('')
  const [raciPreview, setRaciPreview] = useState<boolean>(false)
  const [raci, setRaci] = useState<Raci[]>([])

  useEffect(() => {
    if (!containerRef.current) return

    viewerRef.current = new BpmnViewer({
      container: containerRef.current,
      additionalModules: [SDocumentation],
      moddleExtensions: {
        camunda: CamundaBpmnModdle,
        sistemiv: sistemivModdleDescriptors,
      },
    })

    viewerRef.current.importXML(xml)
    // viewerRef.current.on('documentation.open', (e: any) => console.log(e))
    // viewerRef.current.on('form.open', (e: any) => console.log(e))

    const eventBus = viewerRef.current.get('eventBus')
    eventBus.off('element.click') // Onemogući klikove na elemente
    eventBus.off('element.hover') // Onemogući hover efekte

    return () => {
      viewerRef.current?.destroy()
      viewerRef.current = null
    }
  }, [xml])

  useEffect(() => {
    if (!viewerRef?.current) return

    const formHandler = async (e: any) => {
      const formId = e.formRef
      if (!formId) return

      setIsLoading(true)
      try {
        const blob = await MetadataService.downloadFileByMetadata(org!, project!, 'formId', formId)
        const text = await blob.text()
        const json = JSON.parse(text)

        setParsedJson(json)
        setFormPreview(true)
      } catch (err) {
        console.error('Error downloading or parsing JSON:', err)
      } finally {
        setIsLoading(false)
      }
    }

    const documentationHandler = (e: any) => {
      const markdown = e.markdown
      setMarkdown(markdown)
      setDocumentationPreview(true)
    }

    const raciHandler = (e: { element: any; raci: Raci[] }) => {
      const raci = e.raci
      setRaci(raci)
      setRaciPreview(true)
    }

    // const eventBus = viewerRef.current.get('eventBus')
    // eventBus.on('form.open', formHandler)

    viewerRef.current.on('form.open', formHandler)
    viewerRef.current.on('documentation.open', documentationHandler)
    viewerRef.current.on('raci.open', raciHandler)

    return () => {
      viewerRef.current?.off('form.open', formHandler)
      viewerRef.current?.off('documentation.open', documentationHandler)
      viewerRef.current?.off('raci.open', raciHandler)
    }
  }, [viewerRef, org, project])

  const handleZoom = (step: number) => {
    if (!viewerRef) return
    const zoomScroll = viewerRef.current.get('zoomScroll')
    if (!step) {
      zoomScroll.reset()
      return
    }
    zoomScroll.stepZoom(step)
  }

  return (
    <>
      {isLoading && (
        <div className='absolute inset-0 z-50 bg-gray-200/60 flex items-center justify-center'>
          <SpinnerIcon className='text-sky-500 h-7 w-7 animate-spin' />
        </div>
      )}
      {formPreview && parsedJson && (
        <FormPreviewModal open={formPreview} setOpen={setFormPreview} schema={parsedJson} readOnly={false} />
      )}
      {documentationPreview && markdown && (
        <DocumentationModal open={documentationPreview} setOpen={setDocumentationPreview} markdown={markdown} />
      )}
      {raciPreview && raci.length && <RaciModal open={raciPreview} setOpen={setRaciPreview} raci={raci} />}
      <div className='absolute bottom-3 left-3 flex flex-col-reverse gap-1 z-10'>
        <button className='!p-1 border-gray-200 rounded-sm border-2' onClick={() => handleZoom(-1)}>
          <MinusIcon className='w-5 h-5 stroke-gray-500' />
        </button>
        <button className='!p-1 border-gray-200 rounded-sm border-2' onClick={() => handleZoom(1)}>
          <PlusIcon className='w-5 h-5 stroke-gray-500' />
        </button>
        <button className='!p-1 border-gray-200 rounded-sm border-2' onClick={() => handleZoom(0)}>
          <ViewfinderCircleIcon className='w-5 h-5 stroke-gray-500' />
        </button>
      </div>
      <div ref={containerRef} id='bpmn-viewer' className='absolute inset-0' />
    </>
  )
}

export default BpmnViewerComponent
